import React, { FC, useState, useEffect } from 'react';
import {
  FindingTypeTemplateDetails,
  FindingTypeProperty,
  TypeRepresentations,
  FindingTypeDetails,
  FindingTypePropertyRepresentation
} from '../AnnotationStandardForm.slice';
import FindingTypePropertiesRow from './FindingTypePropertiesRow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import styles from './SelectedFindingType.module.scss';
import classNames from 'classnames';
import { Form, Select } from 'antd';
import { FailureMechanismTemplate } from '../../FailureMechanismTemplate/failureMechanismTemplate.slice';
import { FailureModeCategoryTemplate } from '../../FailureModeCategoryTemplate/failureModeCategoryTemplate.slice';
import { EightDTemplate } from '../../EightDTemplate/eightDTemplate.slice';
import { getInitialShowValue } from '../helpers';

export const LOCALIZATION_FIELDS = [
  'DistanceToRootM',
  'DistanceToLeadingEdgeM',
  'HomographyShape',
  'ShapeOnCanvas',
  'WidthMm',
  'HeightMm',
  'AreaMm2',
  'LengthMm'
];

const { Option } = Select;

type Props = {
  createdFindingType: FindingTypeDetails | undefined;
  findingType: FindingTypeTemplateDetails;
  marginTop: string | number;
  canEdit: boolean;
  failureMechanismTemplates: FailureMechanismTemplate[];
  failureModeCategoryTemplates: FailureModeCategoryTemplate[];
  eightDTemplates: EightDTemplate[];
  onPropertiesChanged: (findingTypeTemplateId: string, representations: TypeRepresentations) => void;
  onRemoveType: (findingTypeTemplateId: string) => void;
  onFailureMechanismChanged: (findingTypeTemplateId: string, selectedIds: string[]) => void;
  onFailureModeCategoryChanged: (findingTypeTemplateId: string, selectedIds: string[]) => void;
  onEightDChanged: (findingTypeTemplateId: string, selectedIds: string[]) => void;
};

const SelectedFindingType: FC<Props> = ({
  createdFindingType,
  findingType,
  marginTop,
  failureMechanismTemplates,
  failureModeCategoryTemplates,
  eightDTemplates,
  onPropertiesChanged,
  onRemoveType,
  canEdit,
  onFailureMechanismChanged,
  onFailureModeCategoryChanged,
  onEightDChanged
}) => {
  const [representation, setRepresentation] = useState<TypeRepresentations>({
    representations: createdFindingType ? createdFindingType.representations : []
  });
  const [fMTemplateIds, setFMTemplateIds] = useState<string[]>(createdFindingType ? createdFindingType.failureMechanismTemplateIds : []);
  const [fModeCTemplateIds, setFModeCTemplateIds] = useState<string[]>(createdFindingType ? createdFindingType.failureModeCategoryTemplateIds : []);
  const [eightDTemplateIds, setEightDTemplateIds] = useState<string[]>(
    createdFindingType && createdFindingType.eightDTemplateIds ? createdFindingType.eightDTemplateIds : []
  );

  useEffect(() => {
    if (findingType) {
      if (representation && representation.representations && representation.representations.length) {
        return;
      }

      let newRepresentation = { representations: [] } as TypeRepresentations;
      findingType.properties.forEach(property => {
        const newPropRepresentation: FindingTypePropertyRepresentation = {
          findingTypePropertyId: property.id,
          id: undefined,
          title: property.fullName,
          unit: '',
          group: LOCALIZATION_FIELDS.includes(property.name) ? 'Localization' : '',
          isMandatory: false,
          show: getInitialShowValue(property.name)
        };
        newRepresentation.representations.push(newPropRepresentation);
      });
      setRepresentation(newRepresentation);
      onPropertiesChanged(findingType.id, newRepresentation);
    }
  }, []);

  const onRowChanged = (
    findingTypePropertyId: string,
    id: string | undefined,
    title: string,
    unit: string,
    group: string,
    mandatory: boolean,
    show: boolean
  ) => {
    let newRepresentation = {} as TypeRepresentations;
    const propRepresentation: FindingTypePropertyRepresentation = {
      id,
      findingTypePropertyId,
      title,
      unit,
      group,
      isMandatory: mandatory,
      show
    };

    if (!representation) {
      newRepresentation = {
        representations: [propRepresentation]
      };
    } else {
      newRepresentation = {
        representations: [...representation.representations.filter(x => x.findingTypePropertyId !== findingTypePropertyId), propRepresentation]
      };
    }

    setRepresentation(newRepresentation);
    onPropertiesChanged(findingType.id, newRepresentation);
  };

  const generateProperties = (properties: Array<FindingTypeProperty>) => {
    return properties.map(property => (
      <FindingTypePropertiesRow
        key={property.id}
        onRowChanged={onRowChanged}
        property={property}
        representation={representation.representations.find(x => x.findingTypePropertyId === property.id)}
        canEdit={canEdit}
      />
    ));
  };

  const failureMechanismChanged = (ids: string[]) => {
    setFMTemplateIds(ids);
    onFailureMechanismChanged(findingType.id, ids);
  };

  const failureModeCategoryChanged = (ids: string[]) => {
    setFModeCTemplateIds(ids);
    onFailureModeCategoryChanged(findingType.id, ids);
  };

  const eightDChanged = (ids: string[]) => {
    setEightDTemplateIds(ids);
    onEightDChanged(findingType.id, ids);
  };

  const filteredDMTemplatedIds = fMTemplateIds.filter(x => !!failureMechanismTemplates.find(y => y.id === x));
  const filteredFModeCTemplatesIds = fModeCTemplateIds.filter(x => !!failureModeCategoryTemplates.find(y => y.id === x));
  const filteredEightDTemplateIds = eightDTemplateIds.filter(x => !!eightDTemplates.find(y => y.id === x));
  return (
    <div key={findingType.id} style={{ marginTop: marginTop, border: '1px solid #d3d3d361', padding: '10px' }}>
      <div className={styles.titleContainer}>
        <div className={styles.title}>
          <h4>
            {findingType.isMLRelated ? '(ML) ' : ''}
            {findingType.name} {findingType.version}
          </h4>
          <FontAwesomeIcon
            className={classNames(styles.remove, {
              [styles.disabled]: !canEdit
            })}
            onClick={() => canEdit && onRemoveType(findingType.id)}
            icon={faTimes}
          />
        </div>
        <div style={{ display: 'flex', width: '750px', justifyContent: 'space-between' }}>
          <div style={{ width: '200px' }}>
            <Form.Item label='Failure Mechanisms:'>
              <Select
                disabled={!canEdit}
                showSearch
                style={{ width: 200 }}
                placeholder='Please select'
                optionFilterProp='children'
                onChange={failureMechanismChanged}
                filterOption={true}
                mode='multiple'
                value={filteredDMTemplatedIds}
                maxTagCount={0}
              >
                {failureMechanismTemplates.map(type => (
                  <Option value={type.id!} key={type.id!}>
                    {type.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div style={{ width: '200px' }}>
            <Form.Item label='Failure Mode Categories:'>
              <Select
                disabled={!canEdit}
                showSearch
                style={{ width: 200 }}
                placeholder='Please select'
                optionFilterProp='children'
                onChange={failureModeCategoryChanged}
                filterOption={true}
                mode='multiple'
                value={filteredFModeCTemplatesIds}
                maxTagCount={0}
              >
                {failureModeCategoryTemplates.map(type => (
                  <Option value={type.id!} key={type.id!}>
                    {type.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div style={{ width: '200px' }}>
            <Form.Item label='8D:'>
              <Select
                disabled={!canEdit}
                showSearch
                style={{ width: 200 }}
                placeholder='Please select'
                optionFilterProp='children'
                onChange={eightDChanged}
                filterOption={true}
                mode='multiple'
                value={filteredEightDTemplateIds}
                maxTagCount={0}
              >
                {eightDTemplates.map(type => (
                  <Option value={type.id!} key={type.id!}>
                    {type.code}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </div>
      </div>
      <table className='custom-table' style={{ margin: '0 auto' }}>
        <thead>
          <tr>
            <th>Property Name</th>
            <th>Title</th>
            <th>Unit</th>
            <th>Group</th>
            <th>Mandatory</th>
            <th>Show</th>
          </tr>
        </thead>
        <tbody>{generateProperties(findingType.properties)}</tbody>
      </table>
    </div>
  );
};

export default SelectedFindingType;
